import { useSyncExternalStore } from 'react'

type ThemeMode = 'light' | 'dark'

const STORAGE_KEY = 'theme_mode'

function getSystemPreference(): ThemeMode {
  return window.matchMedia('(prefers-color-scheme: dark)').matches
    ? 'dark'
    : 'light'
}

function getStoredMode(): ThemeMode {
  const stored = localStorage.getItem(STORAGE_KEY)
  if (stored === 'light' || stored === 'dark') return stored
  return getSystemPreference()
}

let currentMode: ThemeMode = getStoredMode()
const listeners = new Set<() => void>()

function emitChange() {
  for (const listener of listeners) {
    listener()
  }
}

function subscribe(listener: () => void) {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

function getSnapshot(): ThemeMode {
  return currentMode
}

function applyThemeAttribute(mode: ThemeMode) {
  document.documentElement.setAttribute('data-theme', mode)
  document.documentElement.style.backgroundColor =
    mode === 'dark' ? '#0e101a' : '#ffffff'
}

applyThemeAttribute(currentMode)

function toggleMode() {
  currentMode = currentMode === 'dark' ? 'light' : 'dark'
  localStorage.setItem(STORAGE_KEY, currentMode)
  applyThemeAttribute(currentMode)
  emitChange()
}

export function useThemeMode() {
  const mode = useSyncExternalStore(subscribe, getSnapshot)
  return { mode, toggleMode, isDark: mode === 'dark' }
}
